import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch, fmtDate } from "../lib/api";
import { Card, Empty, StateBadge } from "./common";

const today = () => new Date().toLocaleDateString("en-CA");
const range = (day) => {
  const s = new Date(`${day}T00:00:00`);
  return `start=${encodeURIComponent(s.toISOString())}&end=${encodeURIComponent(new Date(s.getTime() + 86400000).toISOString())}`;
};

const ReplyCell = ({ r, id }) => r
  ? <span data-testid={`sent-reply-${id}`} className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">{(r.classification || "reply").replaceAll("_", " ")} · {fmtDate(r.at)}</span>
  : <span data-testid={`sent-reply-${id}`} className="text-[11px] text-slate-400">no reply yet</span>;

export function SentTracker() {
  const [day, setDay] = useState(today());
  const { data } = useFetch(`/outreach/sent-log?day=${day}&${range(day)}`, [day]);
  const items = data?.items || [];
  return (
    <Card title={`Automatic emails sent · ${items.length}`} className="mt-6" testid="sent-tracker"
      action={<input type="date" data-testid="sent-tracker-day" className="dm-input h-8 text-xs" value={day} max={today()} onChange={(e) => setDay(e.target.value || today())} />}>
      {!items.length ? <Empty text="No automatic emails sent on this day." testid="sent-tracker-empty" /> : (
        <div className="overflow-auto"><table className="dm-table w-full"><thead><tr><th>Time</th><th>Company</th><th>To</th><th>Match</th><th>Why it matched</th><th>Reply</th></tr></thead>
          <tbody>{items.map((x) => (
            <tr key={x.id} data-testid={`sent-row-${x.id}`}>
              <td className="whitespace-nowrap text-xs">{fmtDate(x.sent_at)}</td>
              <td className="text-xs"><Link className="text-blue-700 hover:underline" to={`/admin/opportunities/${x.opportunity_id}`}>{x.company}</Link><div className="text-[11px] text-slate-500">{x.type.replaceAll("_", " ")} · {x.country}</div></td>
              <td className="font-mono text-[11px]">{x.to_email}</td>
              <td className="text-xs">{x.match_band || "—"} · {x.score ?? "—"}/100 {x.status === "bounced" && <StateBadge s="bounced" />}</td>
              <td className="max-w-md text-[11px] text-slate-600">{x.match_reason || "—"}</td>
              <td><ReplyCell r={x.reply} id={x.id} /></td>
            </tr>))}</tbody></table></div>)}
    </Card>
  );
}

const Table = ({ title, rows, testid, limit }) => (
  <Card title={title} testid={testid}>
    {!rows?.length ? <Empty text="No outreach sent yet." /> : (
      <table className="dm-table w-full"><thead><tr><th>{title.includes("country") ? "Country" : "Service"}</th><th>Sent</th><th>Replies</th><th>Interested</th><th>Reply rate</th></tr></thead>
        <tbody>{rows.slice(0, limit).map((r) => <tr key={r.key}><td className="text-xs">{r.key}</td><td className="text-xs">{r.sent}</td><td className="text-xs">{r.replies}</td><td className="text-xs">{r.positive}</td><td className="text-xs font-semibold">{r.reply_rate}%</td></tr>)}</tbody></table>)}
  </Card>
);

export function OutreachResults({ compact = false }) {
  const { data } = useFetch("/reports/outreach-results");
  if (!data) return null;
  const t = data.total;
  return (
    <div className={compact ? "mt-6" : "mb-6"} data-testid={compact ? "results-summary" : "results-report"}>
      <div className="mb-3 text-sm text-slate-600" data-testid="results-total">Outreach results: <b>{t.sent}</b> sent · <b>{t.replies}</b> replies · <b>{t.positive}</b> interested · <b>{t.reply_rate}%</b> reply rate
        {compact && <Link to="/admin/reports" className="ml-2 text-blue-700 hover:underline">Full report</Link>}</div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Table title="Replies by service" rows={data.by_service} testid="results-by-service" limit={compact ? 3 : 50} />
        <Table title="Replies by country" rows={data.by_country} testid="results-by-country" limit={compact ? 3 : 50} />
      </div>
    </div>
  );
}
